import { useMutation, useQuery } from "@apollo/client";
import { Route, Routes as RouterRoutes, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import routes from "./routes";
import { getChangedFields, Portal } from "../../../../util";
import AuthRoute from "../../../../components/AuthRoute";
import { FormSaveStatus, NavBar, Spinner } from "../../../../components/ui";
import useAutosaveFormik from "../../../../hooks/useAutosaveFormik";
import { FETCH_ARTWORK as fetchArtwork } from "../../../../graphql/query/Artwork";
import { UPDATE_ARTWORK as updateArtworkMutation } from "../../../../graphql/mutation/Artwork";
import { createModuleErrorBoundary } from "../../../../components/ErrorBoundary";

const Body = props => {
  const { loading, ...remainingProps } = props;

  return loading ? (
    <div className="h-100% flex w-full items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <Sentry.ErrorBoundary
      fallback={createModuleErrorBoundary("artwork-wizard")}
      beforeCapture={scope => {
        scope.setTag("module", "artwork-wizard");
      }}
    >
      <RouterRoutes>
        {routes
          ?.filter?.(item => item?.element)
          ?.map((item, index) => {
            return (
              <Route
                key={index}
                path={item?.href}
                element={
                  <AuthRoute permissions={item.permissions}>
                    <item.element {...remainingProps} />
                  </AuthRoute>
                }
              />
            );
          })}
      </RouterRoutes>
    </Sentry.ErrorBoundary>
  );
};

const ArtworkForm = props => {
  const { id } = useParams();

  const { data: { artwork } = {}, loading } = useQuery(
    fetchArtwork({
      // Scalar fields used in Details page forms
      categoryId: true,
      classId: true,
      confidential: true,
      countryOfOriginId: true,
      description: true,
      dimensions: true,
      goodsCode: true,
      medium: true,
      photoLink: true,
      precis: true,
      series: true,
      standardId: true,
      timestamp: true,
      title: true,
      videoLink: true,
      photoNote: true,
      // Technical page fields
      installation: true,
      authenticity: true,
      maintenance: true,
      packing: true,
      technicalDimensions: true,
      installationLink: true,
      // Association fields
      artists: true,
      images: {
        imgT: {},
        photoMaterial: {},
        uploadedAt: true,
        order: true,
        description: true,
        channelDisplayOnline: true,
      },
      editions: {
        availableNumbers: true,
        type: {},
        year: true,
        number: true,
        ap: true,
      },
      imagesSummary: { imgT: {} },
    }),
    {
      variables: { id: +id },
    },
  );
  const [updateArtwork] = useMutation(updateArtworkMutation());

  const formik = useAutosaveFormik({
    initialValues: artwork,
    onSave: async (values, savedValues) => {
      const changedFields = getChangedFields(values, savedValues);

      if (Object.keys(changedFields).length > 0) {
        const { data } = await updateArtwork({
          variables: {
            input: {
              id: +id,
              ...changedFields,
            },
          },
        });

        if (!data?.updateArtwork?.success) {
          throw new Error(
            data?.updateArtwork?.error || "The artwork could not be saved",
          );
        }
      }
    },
  });

  const bodyProps = {
    ...props,
    artwork,
    artworkId: +id,
    formik,
  };

  return (
    <div className="relative flex min-h-full">
      <Portal id="form-save-status">
        <FormSaveStatus state={formik.autosave.formState} />
      </Portal>
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <div className="flex min-w-0 flex-1 overflow-x-auto py-4 lg:py-8">
        <Body {...bodyProps} loading={loading} />
      </div>
    </div>
  );
};

const Wizard = props => {
  const { id } = useParams();

  return <ArtworkForm key={id} {...props} />;
};

export default Wizard;
